/**
 * BULK ACTIONS BAR - Universal Work Items Bulk Operations
 * 
 * A reusable component for bulk operations across all work item views:
 * - WorkItemsDataGrid (List view)
 * - WorkItemsKanbanBoard (Board view)
 * - WorkItemsTimelineView (Gantt/Timeline view)
 * - WorkItemsCalendarView (Calendar view)
 * - WorkItemsViewWrapper (Unified view manager)
 * 
 * Features:
 * - Bulk assign to user/team
 * - Bulk change status
 * - Bulk change priority
 * - Bulk add/remove tags
 * - Bulk set due date
 * - Bulk delete (with confirmation)
 * - Bulk close/archive
 * - Export selected
 * 
 * @version 1.0.0
 * @date February 2026
 */

'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Users, UserPlus, Flag, Tag, Calendar, Trash2, CheckCircle,
  Archive, Download, ChevronDown, Loader2, AlertTriangle,
  Clock, ArrowUp, ArrowDown, Minus, Flame, RotateCcw
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { notify, confirm } from '@/lib/notifications';
import toast from 'react-hot-toast';

// ============================================================================
// TYPES
// ============================================================================

export interface BulkActionUser {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
}

export interface BulkActionsBarProps {
  selectedItems: Set<string>;
  items: Array<{ id: string; item_type?: string; status?: string; title?: string; deleted_at?: string | null }>;
  onClearSelection: () => void;
  onRefresh: () => void;
  // Available options
  availableUsers?: BulkActionUser[];
  availableStatuses?: Array<{ key: string; label: string; color?: string }>;
  availableTags?: string[];
  // Feature toggles
  showAssign?: boolean;
  showStatus?: boolean;
  showPriority?: boolean;
  showTags?: boolean;
  showDueDate?: boolean;
  showDelete?: boolean;
  showRestore?: boolean;
  showClose?: boolean;
  showExport?: boolean;
  // Customization
  primaryColor?: string;
  position?: 'top' | 'bottom';
  // Permissions
  canEdit?: boolean;
  canDelete?: boolean;
  canRestore?: boolean;
  // Custom actions rendered before the standard buttons
  customActions?: React.ReactNode;
}

// Priority options
const PRIORITY_OPTIONS = [
  { key: 'urgent', label: 'Urgent', icon: Flame, color: 'text-red-500' },
  { key: 'high', label: 'High', icon: ArrowUp, color: 'text-orange-500' },
  { key: 'medium', label: 'Medium', icon: Minus, color: 'text-blue-500' },
  { key: 'low', label: 'Low', icon: ArrowDown, color: 'text-gray-500' },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function BulkActionsBar({
  selectedItems,
  items,
  onClearSelection,
  onRefresh,
  availableUsers = [],
  availableStatuses = [],
  availableTags = [],
  showAssign = true,
  showStatus = true,
  showPriority = true,
  showTags = true,
  showDueDate = true,
  showDelete = true,
  showRestore = false,
  showClose = true,
  showExport = true,
  primaryColor = '#6366f1',
  position = 'bottom',
  canEdit = true,
  canDelete = true,
  canRestore = false,
  customActions,
}: BulkActionsBarProps) {
  const [processing, setProcessing] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [newTag, setNewTag] = useState('');
  const [selectedDate, setSelectedDate] = useState('');

  const selectedCount = selectedItems.size;
  const selectedItemsList = useMemo(() => 
    items.filter(item => selectedItems.has(item.id)), 
    [items, selectedItems]
  );
  const selectedDeletedItems = useMemo(
    () => selectedItemsList.filter((item) => Boolean(item.deleted_at)),
    [selectedItemsList]
  );

  // Close dropdown on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveDropdown(null);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (activeDropdown && !(e.target as Element).closest('.bulk-dropdown')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeDropdown]);

  if (selectedCount === 0) return null;

  // ============================================================================
  // API CALLS
  // ============================================================================

  const bulkUpdate = async (updates: Record<string, any>, successMessage: string) => {
    if (!canEdit) {
      notify.error('You do not have permission to edit items');
      return;
    }

    setProcessing(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      const itemIds = Array.from(selectedItems);
      
      const response = await fetch('/api/v1/work-items/bulk', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          item_ids: itemIds,
          updates
        })
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Bulk update failed');
      }

      notify.success(`✓ ${successMessage} (${result.data?.updated || itemIds.length} items)`);
      onClearSelection();
      onRefresh();
    } catch (error: any) {
      notify.error(error.message || 'Bulk operation failed');
    } finally {
      setProcessing(false);
      setActiveDropdown(null);
    }
  };

  const handleBulkAssign = async (userId: number | null) => {
    await bulkUpdate(
      { assigned_to: userId },
      userId ? 'Items assigned' : 'Items unassigned'
    );
  };

  const handleBulkStatus = async (status: string) => {
    await bulkUpdate({ status }, `Status changed to ${status}`);
  };

  const handleBulkPriority = async (priority: string) => {
    await bulkUpdate({ priority }, `Priority set to ${priority}`);
  };

  const handleBulkDueDate = async () => {
    if (!selectedDate) {
      notify.error('Please select a date');
      return;
    }
    await bulkUpdate({ due_date: selectedDate }, 'Due date updated');
    setSelectedDate('');
  };

  const handleBulkAddTag = async (tag: string) => {
    if (!tag.trim()) return;
    
    setProcessing(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      const response = await fetch('/api/v1/work-items/bulk/tags', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          item_ids: Array.from(selectedItems),
          action: 'add',
          tag: tag.trim()
        })
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to add tag');
      }

      notify.success(`✓ Tag "${tag}" added to ${selectedItems.size} items`);
      onClearSelection();
      onRefresh();
      setNewTag('');
    } catch (error: any) {
      notify.error(error.message || 'Failed to add tag');
    } finally {
      setProcessing(false);
      setActiveDropdown(null);
    }
  };

  const handleBulkClose = async () => {
    const confirmed = await confirm({
      title: 'Close Selected Items',
      message: `Are you sure you want to close ${selectedCount} item(s)? This will mark them as completed.`,
      confirmText: 'Close All',
      cancelText: 'Cancel',
      onConfirm: async () => {}
    });

    if (confirmed) {
      await bulkUpdate({ status: 'closed', closed_at: new Date().toISOString() }, 'Items closed');
    }
  };

  const handleBulkDelete = async () => {
    if (!canDelete) {
      notify.error('You do not have permission to delete items');
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Selected Items',
      message: `Are you sure you want to delete ${selectedCount} item(s)? You will have 5 seconds to undo this action.`,
      confirmText: 'Delete All',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: async () => {}
    });

    if (!confirmed) return;

    // Issue D3: undo window for destructive bulk delete.
    // Optimistically clear selection and refresh, then show an undo toast.
    // The actual API call is delayed by 5 seconds; if the user clicks Undo,
    // the delete is cancelled.
    const itemIds = Array.from(selectedItems);
    onClearSelection();

    let cancelled = false;
    const UNDO_WINDOW_MS = 5000;

    // Issue D3: undo toast with react-hot-toast custom render.
    const undoToastId = toast.custom(
      (t) => (
        <div className="flex items-center gap-3 rounded-lg bg-gray-900 px-4 py-3 text-white shadow-lg dark:bg-gray-800">
          <span className="text-sm">{selectedCount} item(s) deleted</span>
          <button
            onClick={() => {
              cancelled = true;
              toast.dismiss(t.id);
              onRefresh();
              notify.success('Delete cancelled — items restored');
            }}
            className="rounded bg-blue-500 px-2 py-1 text-xs font-medium text-white hover:bg-blue-600"
          >
            Undo
          </button>
        </div>
      ),
      { duration: UNDO_WINDOW_MS },
    );

    setTimeout(async () => {
      toast.dismiss(undoToastId);
      if (cancelled) return;
      setProcessing(true);
      try {
        const token = authService.getTokens()?.access_token;
        if (!token) throw new Error('Not authenticated');

        const response = await fetch('/api/v1/work-items/bulk', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            item_ids: itemIds
          })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Bulk delete failed');
        }

        notify.success(`✓ ${result.data?.deleted || itemIds.length} items deleted`);
        onRefresh();
      } catch (error: any) {
        notify.error(error.message || 'Bulk delete failed');
        onRefresh(); // Restore items since the delete failed
      } finally {
        setProcessing(false);
      }
    }, UNDO_WINDOW_MS);
  };

  const handleBulkRestore = async () => {
    if (!canRestore) {
      notify.error('You do not have permission to restore items');
      return;
    }

    if (selectedDeletedItems.length === 0) {
      notify.error('Select at least one deleted item to restore');
      return;
    }

    const confirmed = await confirm({
      title: 'Restore Selected Items',
      message: `Restore ${selectedDeletedItems.length} deleted item(s)? Deleted child work items linked in the hierarchy will also be restored.`,
      confirmText: 'Restore Selected',
      cancelText: 'Cancel',
      onConfirm: async () => {}
    });

    if (!confirmed) return;

    setProcessing(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      const response = await fetch('/api/v1/work-items/bulk', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'restore',
          item_ids: selectedDeletedItems.map((item) => item.id),
          restore_children: true,
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Bulk restore failed');
      }

      const restored = result.data?.restored || selectedDeletedItems.length;
      const cascaded = result.data?.restored_children || 0;
      notify.success(
        cascaded > 0
          ? `✓ Restored ${restored} items (${cascaded} child item${cascaded === 1 ? '' : 's'} via cascade)`
          : `✓ Restored ${restored} items`
      );
      onClearSelection();
      onRefresh();
    } catch (error: any) {
      notify.error(error.message || 'Bulk restore failed');
    } finally {
      setProcessing(false);
      setActiveDropdown(null);
    }
  };

  const handleExport = () => {
    // Create CSV of selected items
    const headers = ['ID', 'Number', 'Title', 'Type', 'Status', 'Priority'];
    const rows = selectedItemsList.map(item => [
      item.id,
      (item as any).item_number || '',
      item.title || '',
      item.item_type || '',
      item.status || '',
      (item as any).priority || ''
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `work-items-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    notify.success(`✓ Exported ${selectedCount} items`);
  };

  // ============================================================================
  // DROPDOWN COMPONENT
  // ============================================================================

  const DropdownButton = ({ 
    id, 
    icon: Icon, 
    label, 
    children,
    disabled = false
  }: { 
    id: string; 
    icon: React.ElementType; 
    label: string; 
    children: React.ReactNode;
    disabled?: boolean;
  }) => (
    <div className="relative bulk-dropdown">
      <button
        onClick={() => !disabled && setActiveDropdown(activeDropdown === id ? null : id)}
        disabled={disabled || processing}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
          activeDropdown === id
            ? 'bg-white/20 text-white'
            : 'text-white/90 hover:bg-white/10'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <Icon className="w-4 h-4" />
        <span className="hidden sm:inline">{label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${activeDropdown === id ? 'rotate-180' : ''}`} />
      </button>
      
      {activeDropdown === id && (
        <div className="absolute bottom-full mb-2 left-0 min-w-[200px] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 z-50 animate-in fade-in slide-in-from-bottom-2">
          {children}
        </div>
      )}
    </div>
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  const barContent = (
    <div 
      className={`fixed ${position === 'bottom' ? 'bottom-4' : 'top-20'} left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 fade-in duration-200`}
    >
      <div 
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl shadow-2xl border border-white/20"
        style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)` }}
      >
        {/* Selection Count */}
        <div className="flex items-center gap-2 pr-3 border-r border-white/20">
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
            <span className="text-white font-bold">{selectedCount}</span>
          </div>
          <span className="text-white/90 text-sm hidden sm:inline">selected</span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {/* Custom Actions */}
          {customActions}

          {/* Assign */}
          {showAssign && canEdit && (
            <DropdownButton id="assign" icon={UserPlus} label="Assign">
              <button
                onClick={() => handleBulkAssign(null)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                <Users className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700 dark:text-gray-300">Unassign</span>
              </button>
              <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
              {availableUsers.length > 0 ? (
                <div className="max-h-[200px] overflow-y-auto">
                  {availableUsers.map(user => {
                    const name = user.first_name 
                      ? `${user.first_name} ${user.last_name || ''}`.trim()
                      : user.email.split('@')[0];
                    return (
                      <button
                        key={user.id}
                        onClick={() => handleBulkAssign(user.id)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                      >
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                          <span className="text-[10px] font-bold text-white">
                            {name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        </div>
                        <span className="text-gray-700 dark:text-gray-300">{name}</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="px-3 py-2 text-sm text-gray-500">No users available</p>
              )}
            </DropdownButton>
          )}

          {/* Status */}
          {showStatus && canEdit && availableStatuses.length > 0 && (
            <DropdownButton id="status" icon={Clock} label="Status">
              {availableStatuses.map(status => (
                <button
                  key={status.key}
                  onClick={() => handleBulkStatus(status.key)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <div className={`w-2 h-2 rounded-full ${status.color || 'bg-gray-400'}`} />
                  <span className="text-gray-700 dark:text-gray-300">{status.label}</span>
                </button>
              ))}
            </DropdownButton>
          )}

          {/* Priority */}
          {showPriority && canEdit && (
            <DropdownButton id="priority" icon={Flag} label="Priority">
              {PRIORITY_OPTIONS.map(priority => (
                <button
                  key={priority.key}
                  onClick={() => handleBulkPriority(priority.key)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <priority.icon className={`w-4 h-4 ${priority.color}`} />
                  <span className="text-gray-700 dark:text-gray-300">{priority.label}</span>
                </button>
              ))}
            </DropdownButton>
          )}

          {/* Tags */}
          {showTags && canEdit && (
            <DropdownButton id="tags" icon={Tag} label="Tags">
              <div className="p-2">
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={newTag}
                    onChange={e => setNewTag(e.target.value)}
                    placeholder="Add tag..."
                    className="flex-1 px-2 py-1 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                    onKeyDown={e => e.key === 'Enter' && handleBulkAddTag(newTag)}
                  />
                  <button
                    onClick={() => handleBulkAddTag(newTag)}
                    className="px-2 py-1 bg-indigo-500 text-white rounded text-sm hover:bg-indigo-600"
                  >
                    Add
                  </button>
                </div>
              </div>
              {availableTags.length > 0 && (
                <>
                  <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                  <div className="max-h-[150px] overflow-y-auto">
                    {availableTags.map(tag => (
                      <button
                        key={tag}
                        onClick={() => handleBulkAddTag(tag)}
                        className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <span className="text-gray-700 dark:text-gray-300">#{tag}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </DropdownButton>
          )}

          {/* Due Date */}
          {showDueDate && canEdit && (
            <DropdownButton id="duedate" icon={Calendar} label="Due Date">
              <div className="p-3">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={e => setSelectedDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                />
                <button
                  onClick={handleBulkDueDate}
                  disabled={!selectedDate}
                  className="w-full mt-2 px-3 py-2 bg-indigo-500 text-white rounded-lg text-sm hover:bg-indigo-600 disabled:opacity-50"
                >
                  Apply Date
                </button>
              </div>
            </DropdownButton>
          )}

          {/* Export */}
          {showExport && (
            <button
              onClick={handleExport}
              disabled={processing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white/90 hover:bg-white/10 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
          )}

          {/* Restore */}
          {showRestore && canRestore && (
            <button
              onClick={handleBulkRestore}
              disabled={processing || selectedDeletedItems.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-emerald-100 hover:bg-emerald-500/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RotateCcw className="w-4 h-4" />
              <span className="hidden sm:inline">Restore</span>
            </button>
          )}

          {/* Close/Complete */}
          {showClose && canEdit && (
            <button
              onClick={handleBulkClose}
              disabled={processing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white/90 hover:bg-white/10 transition-colors"
            >
              <CheckCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Close</span>
            </button>
          )}

          {/* Delete */}
          {showDelete && canDelete && (
            <button
              onClick={handleBulkDelete}
              disabled={processing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-red-200 hover:bg-red-500/30 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline">Delete</span>
            </button>
          )}
        </div>

        {/* Processing Indicator */}
        {processing && (
          <div className="pl-2 border-l border-white/20">
            <Loader2 className="w-5 h-5 animate-spin text-white" />
          </div>
        )}

        {/* Clear Selection */}
        <button
          onClick={onClearSelection}
          disabled={processing}
          className="ml-2 p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          title="Clear selection"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  // Render as portal to body
  if (typeof document !== 'undefined') {
    return createPortal(barContent, document.body);
  }

  return barContent;
}
